import { mergeProps } from "@base-ui/react/merge-props"
import { useRender } from "@base-ui/react/use-render"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "group/badge inline-flex h-5 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-tag border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-all has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 aria-invalid:border-[var(--status-bad)] [&>svg]:pointer-events-none [&>svg]:size-3!",
  {
    variants: {
      variant: {
        // Soft, never a saturated fill: tinted background token + the ramp's dark ink.
        default:
          "bg-[var(--accent-active-bg)] text-[var(--accent-active-text)]",
        // Signal DS tinted status pill (ramp/100 bg + ramp/700 text) — use for
        // positive clinical/state meaning (e.g. an Active record), never the
        // brand-primary badge which reads as a CTA, not a status.
        success:
          "border-transparent bg-[var(--color-status-success-bg)] text-[var(--color-green-700)]",
        secondary:
          "bg-[var(--color-status-neutral-bg)] text-[var(--ink-secondary)]",
        destructive:
          "bg-[var(--color-status-error-bg)] text-[var(--color-red-700)]",
        outline:
          "border-[var(--surface-line)] text-[var(--ink-body)] [a]:hover:bg-[var(--surface-panel)]",
        ghost:
          "text-[var(--ink-secondary)] hover:bg-[var(--surface-panel)]",
        link: "text-primary underline-offset-4 hover:underline",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant = "default",
  render,
  ...props
}: useRender.ComponentProps<"span"> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: cn(badgeVariants({ variant }), className),
      },
      props
    ),
    render,
    state: {
      slot: "badge",
      variant,
    },
  })
}

export { Badge, badgeVariants }
